import CheckIcon from '@mui/icons-material/Check';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { ColDef } from '@siloreal:modules:private/silobag-list/config/extra/types';
import * as HOOKS from '@siloreal:shared:hooks';

const CAPSULE_WIDTH = 58;
const CAPSULE_HEIGHT = 28;
const ACCENT_WIDTH = 3;

export function buildAlertColumn(): ColDef {
  return {
    field: 'alertas',
    headerName: HOOKS.useLocale.getState().t('temp.common.alerts'),
    headerAlign: 'center',
    minWidth: CAPSULE_WIDTH + 12,
    sortable: false,
    renderCell: ({ row }) => (
      <AlertStatusCapsule hex={row.hex} shortLabel={row.short_name_last_alert} severity={row.severity} />
    ),
  };
}

function AlertStatusCapsule({
  hex,
  shortLabel,
  severity,
}: {
  hex: string;
  shortLabel: string | null;
  severity: number;
}) {
  const i18n = HOOKS.useLocale();
  const isOk = shortLabel == null || severity === 0;
  const label = shortLabel?.trim() ?? '';
  const textColor = severity === 1 ? '#1a1612' : '#ffffff';
  const tooltip_ok = i18n.t('temp.common.tooltip_sb_optimal');
  const tooltip_monitoring_alert = i18n.t('temp.common.tooltip_sb_monitoring_alert');
  const tooltip = isOk ? tooltip_ok : `${label} · ${tooltip_monitoring_alert}.`;

  return (
    <Stack
      height="100%"
      width="100%"
      direction="row"
      alignItems="center"
      justifyContent="center"
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title={tooltip} placement="top" arrow disableHoverListener={!isOk && !label}>
        <Box
          aria-label={tooltip}
          sx={{
            width: CAPSULE_WIDTH,
            height: CAPSULE_HEIGHT,
            display: 'inline-flex',
            alignItems: 'stretch',
            borderRadius: '5px',
            overflow: 'hidden',
            border: '1px solid',
            borderColor: shadeHex(hex, 0.28, 'darker'),
            backgroundColor: hex,
            boxShadow: '0 1px 2px rgba(22, 28, 20, 0.08), inset 0 1px 0 rgba(255,255,255,0.35)',
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: ACCENT_WIDTH,
              flexShrink: 0,
              backgroundColor: shadeHex(hex, 0.18, 'darker'),
            }}
          />

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: isOk ? '#ffffff' : textColor,
            }}
          >
            {isOk ? (
              <CheckIcon
                sx={{
                  fontSize: `${16 / 14}em`,
                  '& path': {
                    stroke: 'currentColor',
                    strokeWidth: 0,
                    strokeLinecap: 'round',
                    strokeLinejoin: 'round',
                  },
                }}
              />
            ) : (
              <Typography
                component="span"
                title={label}
                sx={{
                  fontFamily: '"Roboto Mono", "SF Mono", monospace',
                  fontWeight: 800,
                  fontSize: `${11 / 14}em`,
                  letterSpacing: '0.1em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  color: 'inherit',
                }}
              >
                {label}
              </Typography>
            )}
          </Box>
        </Box>
      </Tooltip>
    </Stack>
  );
}

function shadeHex(hex: string, factor: number, direction: 'lighter' | 'darker'): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;

  const channel = (value: number) => {
    if (direction === 'lighter') {
      return Math.min(255, Math.round(value + (255 - value) * factor));
    }

    return Math.max(0, Math.round(value * (1 - factor)));
  };

  return `#${[channel(rgb.r), channel(rgb.g), channel(rgb.b)]
    .map((value) => value.toString(16).padStart(2, '0'))
    .join('')}`;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const normalized = hex.replace('#', '').trim();
  const expanded =
    normalized.length === 3
      ? normalized
          .split('')
          .map((char) => `${char}${char}`)
          .join('')
      : normalized;

  if (expanded.length !== 6) return null;

  return {
    r: Number.parseInt(expanded.slice(0, 2), 16),
    g: Number.parseInt(expanded.slice(2, 4), 16),
    b: Number.parseInt(expanded.slice(4, 6), 16),
  };
}
